import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Search, Plus, GraduationCap, Trash2, Save } from "lucide-react";

export const TargetProdiModal = ({ open, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [custom, setCustom] = useState({ ptn: "", prodi: "", target: 75 });
  const [recommended, setRecommended] = useState([]);
  const [recBidang, setRecBidang] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get("/prodi/catalog").then((r) => setCatalog(r.data.catalog)).catch(() => {});
    api.get("/student/prodi").then((r) => setItems(r.data.items || [])).catch(() => {});
    api.get("/prodi/recommended").then((r) => { setRecommended(r.data.recommended || []); setRecBidang(r.data.bidang || []); }).catch(() => {});
  }, [open]);

  if (!open) return null;

  const has = (p) => items.some((i) => i.ptn === p.ptn && i.prodi === p.prodi);
  const add = (p) => {
    if (items.length >= 4) { toast.error("Maksimal 4 prodi"); return; }
    if (has(p)) return;
    setItems([...items, { ptn: p.ptn, prodi: p.prodi, kelompok: p.kelompok || "", target: p.target || 0, custom: !!p.custom }]);
  };
  const remove = (idx) => setItems(items.filter((_, i) => i !== idx));
  const addCustom = () => {
    if (!custom.ptn.trim() || !custom.prodi.trim()) { toast.error("Isi PTN & prodi dulu"); return; }
    add({ ...custom, kelompok: "", custom: true });
    setCustom({ ptn: "", prodi: "", target: 75 });
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/student/prodi", { items });
      toast.success("Target prodi tersimpan");
      onSaved && onSaved(items);
      onClose();
    } catch { toast.error("Gagal menyimpan"); }
    finally { setSaving(false); }
  };

  const filtered = catalog.filter((p) => `${p.ptn} ${p.prodi} ${p.kelompok}`.toLowerCase().includes(q.toLowerCase())).slice(0, 40);

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="target-prodi-modal" onClick={() => !saving && onClose()}>
      <div className="clay-card p-6 bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2"><GraduationCap className="h-5 w-5 text-[#E25E3E]" /> Target Prodi (maks 4)</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#1A1C1A]/5"><X className="h-4 w-4" /></button>
        </div>
        <p className="text-xs text-[#5C5F5C] mb-4">Angka target = <b>estimasi keketatan</b> (target % benar di TryOut), <b>bukan</b> passing grade/kelulusan resmi SNPMB.</p>

        {recommended.length > 0 && (
          <div className="rounded-xl bg-[#4A6B53]/10 border border-[#4A6B53]/30 p-3 mb-4" data-testid="prodi-recommended">
            <p className="text-xs font-semibold text-[#4A6B53] mb-2">✨ Rekomendasi dari hasil asesmenmu{recBidang.length ? ` (minat: ${recBidang.join(", ")})` : ""}</p>
            <div className="flex flex-wrap gap-2">
              {recommended.map((p, i) => {
                const dis = has(p) || items.length >= 4;
                return (
                  <button key={i} onClick={() => add(p)} disabled={dis} data-testid={`rec-prodi-${i}`}
                    className="text-xs px-3 py-1.5 rounded-full bg-white border border-[#4A6B53]/40 text-[#1A1C1A] hover:bg-[#4A6B53]/10 disabled:opacity-40 flex items-center gap-1">
                    <span className="font-medium">{p.prodi}</span><span className="text-[#5C5F5C]">· {p.ptn.replace("Universitas ", "Univ. ")}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mb-4 space-y-2" data-testid="selected-prodi">
          {items.length === 0 && <p className="text-sm text-[#5C5F5C]">Belum ada prodi dipilih.</p>}
          {items.map((it, idx) => (
            <div key={idx} className="flex items-center justify-between rounded-xl border border-[#E5E0D8] px-3 py-2">
              <div className="min-w-0"><p className="text-sm font-medium text-[#1A1C1A] truncate">{it.prodi}</p><p className="text-xs text-[#5C5F5C] truncate">{it.ptn} · target ±{it.target}</p></div>
              <button onClick={() => remove(idx)} data-testid={`remove-prodi-${idx}`} className="p-1.5 rounded-full text-[#5C5F5C] hover:text-[#D9381E] hover:bg-[#D9381E]/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>

        <div className="relative mb-2">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5F5C]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="prodi-search" placeholder="Cari PTN / prodi…" className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
        </div>
        <div className="max-h-48 overflow-y-auto space-y-1 mb-4 border border-[#E5E0D8] rounded-lg p-2">
          {filtered.map((p, i) => (
            <button key={i} onClick={() => add(p)} disabled={has(p) || items.length >= 4} data-testid={`catalog-prodi-${i}`}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F0ECE4] disabled:opacity-40 flex items-center justify-between">
              <span className="text-sm text-[#1A1C1A]">{p.prodi} <span className="text-xs text-[#5C5F5C]">· {p.ptn}</span></span>
              <span className="text-xs font-medium text-[#E25E3E]">±{p.target}</span>
            </button>
          ))}
          {filtered.length === 0 && <p className="text-xs text-[#5C5F5C] px-2 py-1">Tidak ada hasil. Tambah manual di bawah.</p>}
        </div>

        <div className="rounded-xl bg-[#FAF8F5] p-3 mb-4">
          <p className="text-xs font-semibold text-[#5C5F5C] mb-2">Tambah manual (jika tak ada di daftar)</p>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <input value={custom.ptn} onChange={(e) => setCustom({ ...custom, ptn: e.target.value })} data-testid="custom-ptn" placeholder="PTN" className="px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
            <input value={custom.prodi} onChange={(e) => setCustom({ ...custom, prodi: e.target.value })} data-testid="custom-prodi" placeholder="Prodi" className="px-3 py-2 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs text-[#5C5F5C]">Target %</label>
            <input type="number" min={0} max={100} value={custom.target} onChange={(e) => setCustom({ ...custom, target: Number(e.target.value) })} data-testid="custom-target" className="w-20 px-2 py-1.5 rounded-lg border border-[#E5E0D8] outline-none focus:border-[#E25E3E] text-sm" />
            <button onClick={addCustom} data-testid="add-custom-prodi" className="ml-auto px-3 py-1.5 rounded-full bg-[#1A1C1A] text-white text-xs flex items-center gap-1 hover:opacity-90"><Plus className="h-3.5 w-3.5" /> Tambah</button>
          </div>
        </div>

        <button onClick={save} disabled={saving} data-testid="save-target-prodi" className="w-full py-3 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] disabled:opacity-60 flex items-center justify-center gap-2"><Save className="h-4 w-4" /> {saving ? "Menyimpan…" : "Simpan Target"}</button>
      </div>
    </div>
  );
};

export default TargetProdiModal;
